import React, { useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	HStack,
	IconButton,
	Image,
	Progress,
	Stack,
	Text,
	useToast,
} from "@chakra-ui/react";
import { FiArrowRight, FiPlus, FiTrash2 } from "react-icons/fi";
import { useQuery, useMutation } from "@apollo/client";
import { GET_MY_PORTFOLIO_WITH_MEDIA } from "src/common/graphql/typed-queries";
import {
	EDIT_EMPLOYMENT_HISTORY_ITEM,
	EDIT_PROJECT_HISTORY,
} from "src/common/graphql/typed-mutations";
import { Loading } from "src/components/Loading";
import { RequestFailed } from "src/components/RequestFailed";
import { logEvent } from "src/utlis/analytics";

interface PrefillImagesProps {
	onComplete: () => void;
}

interface PendingImage {
	name: string;
	data: string;
	mimeType: string;
	preview: string;
}

interface ItemImageState {
	pendingImages: PendingImage[];
}

export const PrefillImages: React.FC<PrefillImagesProps> = ({ onComplete }) => {
	const toast = useToast();
	const { data, loading, error } = useQuery(GET_MY_PORTFOLIO_WITH_MEDIA);

	const [itemStates, setItemStates] = useState<Record<string, ItemImageState>>(
		{}
	);
	const [isSavingAll, setIsSavingAll] = useState(false);

	const [editEmployment] = useMutation(EDIT_EMPLOYMENT_HISTORY_ITEM, {
		refetchQueries: ["MyPortfolioWithMedia"],
	});
	const [editWorkHistory] = useMutation(EDIT_PROJECT_HISTORY, {
		refetchQueries: ["MyPortfolioWithMedia"],
	});
	useEffect(() => {
		logEvent("photo_attachment_viewed", { platform: "web" });
	}, []);

	const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

	const getPendingImages = (id: string): PendingImage[] =>
		itemStates[id]?.pendingImages ?? [];

	const handleFileSelect = (
		e: React.ChangeEvent<HTMLInputElement>,
		itemId: string
	) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;

		const current = getPendingImages(itemId);
		const toAdd = files.slice(0, 5 - current.length);

		toAdd.forEach((file) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (typeof reader.result !== "string") return;

				const base64 = reader.result.split(",")[1];
				const pending: PendingImage = {
					name:
						"prefill-image-" +
						base64.substring(0, 5) +
						`-${Math.random() * 1000}`,
					data: base64,
					mimeType: file.type,
					preview: reader.result,
				};

				setItemStates((prev) => ({
					...prev,
					[itemId]: {
						pendingImages: [
							...(prev[itemId]?.pendingImages ?? []),
							pending,
						],
					},
				}));
			};
			reader.readAsDataURL(file);
		});

		if (fileInputRefs.current[itemId]) {
			fileInputRefs.current[itemId]!.value = "";
		}
	};

	const removeImage = (itemId: string, index: number) => {
		setItemStates((prev) => ({
			...prev,
			[itemId]: {
				pendingImages: getPendingImages(itemId).filter(
					(_, i) => i !== index
				),
			},
		}));
	};

	const handleDone = async () => {
		const workHistoryItems = data?.myPortfolio?.workHistory ?? [];
		const employmentItems = data?.myPortfolio?.employmentHistory ?? [];

		const workPending = workHistoryItems.filter(
			(item) => getPendingImages(item.id!).length > 0
		);
		const employmentPending = employmentItems.filter(
			(item) => getPendingImages(item.id).length > 0
		);

		if (!workPending.length && !employmentPending.length) {
			onComplete();
			return;
		}

		setIsSavingAll(true);

		try {
			await Promise.all([
				...workPending.map((item) =>
					editWorkHistory({
						variables: {
							id: item.id!,
							existingImageIds: item.images
								.filter((img) => !img.deletedAt)
								.map((img) => img.id),
							images: getPendingImages(item.id!).map((img) => ({
								name: img.name,
								data: img.data,
								mimeType: img.mimeType,
							})),
						},
					})
				),
				...employmentPending.map((item) =>
					editEmployment({
						variables: {
							id: item.id,
							existingImageIds: item.images
								.filter((img) => !img.deletedAt)
								.map((img) => img.id),
							images: getPendingImages(item.id).map((img) => ({
								name: img.name,
								data: img.data,
								mimeType: img.mimeType,
							})),
						},
					})
				),
			]);

			const totalPhotos = [...workPending, ...employmentPending].reduce(
				(sum, item) => sum + getPendingImages(item.id!).length,
				0
			);
			logEvent("work_history_photos_uploaded", {
				entry_count: workPending.length + employmentPending.length,
				photo_count: totalPhotos,
				platform: "web",
			});

			onComplete();
		} catch {
			setIsSavingAll(false);
			toast({
				title: "Failed to save photos",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsSavingAll(false);
		}
	};

	if (loading) return <Loading />;
	if (error) return <RequestFailed />;

	const workHistoryItems = data?.myPortfolio?.workHistory ?? [];
	const employmentItems = data?.myPortfolio?.employmentHistory ?? [];
	const hasItems = workHistoryItems.length > 0 || employmentItems.length > 0;

	const hasPendingImages = [...workHistoryItems, ...employmentItems].some(
		(item) => getPendingImages(item.id!).length > 0
	);

	return (
		<Stack
			spacing={0}
			className="flex flex-col lg:border-[1px] border-[#C9C9C9] bg-white h-auto lg:min-h-[70vh] justify-between"
		>
			<Stack
				spacing={6}
				px={{ base: 4, lg: 8 }}
				py={{ base: 6, lg: 12 }}
				overflowY="auto"
				flex={1}
			>
				<Box>
					<Text fontSize="2xl" fontWeight="bold" color="#121212">
						Add photos to your work history
					</Text>
					<Text color="gray.600" mt={1}>
						Profiles with photos of completed work get significantly more
						attention from clients. You can skip this and add photos later.
					</Text>
				</Box>

				{!hasItems && (
					<Text color="gray.500" fontSize="sm">
						No project history or employment items found.
					</Text>
				)}

				{workHistoryItems.length > 0 && (
					<Box>
						<Text fontWeight="bold" fontSize="lg" mb={3}>
							Project History
						</Text>
						<Stack spacing={4}>
							{workHistoryItems.map((item) => {
								const id = item.id!;
								const pending = getPendingImages(id);
								const existingCount = item.images.filter(
									(img) => !img.deletedAt
								).length;

								return (
									<ItemImageCard
										key={id}
										title={item.name}
										existingCount={existingCount}
										pendingImages={pending}
										isSavingAll={isSavingAll}
										canAddMore={pending.length < 5}
										onAddClick={() => fileInputRefs.current[id]?.click()}
										onRemove={(index) => removeImage(id, index)}
										inputRef={(el) => {
											fileInputRefs.current[id] = el;
										}}
										onFileSelect={(e) => handleFileSelect(e, id)}
									/>
								);
							})}
						</Stack>
					</Box>
				)}

				{employmentItems.length > 0 && (
					<Box>
						<Text fontWeight="bold" fontSize="lg" mb={3}>
							Employment History
						</Text>
						<Stack spacing={4}>
							{employmentItems.map((item) => {
								const id = item.id;
								const pending = getPendingImages(id);
								const existingCount = item.images.filter(
									(img) => !img.deletedAt
								).length;

								return (
									<ItemImageCard
										key={id}
										title={`${item.jobTitle} at ${item.companyName}`}
										existingCount={existingCount}
										pendingImages={pending}
										isSavingAll={isSavingAll}
										canAddMore={pending.length < 5}
										onAddClick={() => fileInputRefs.current[id]?.click()}
										onRemove={(index) => removeImage(id, index)}
										inputRef={(el) => {
											fileInputRefs.current[id] = el;
										}}
										onFileSelect={(e) => handleFileSelect(e, id)}
									/>
								);
							})}
						</Stack>
					</Box>
				)}
			</Stack>

			<Stack
				py={{ base: 4, lg: 8 }}
				px={{ base: 4, lg: 8 }}
				borderTop={{ base: "none", lg: "1px solid #E7E7E7" }}
				direction={{ base: "column", lg: "row" }}
				spacing={4}
				justifyContent={{ base: "stretch", lg: "flex-end" }}
				alignItems={{ base: "stretch", lg: "center" }}
			>
				<Button
					variant="outline"
					colorScheme="earthRed"
					rounded={0}
					minH={12}
					px={5}
					py={2}
					onClick={() => {
						logEvent("photo_step_skipped", { platform: "web" });
						onComplete();
					}}
					isDisabled={isSavingAll}
					w={{ base: "full", lg: "auto" }}
				>
					Skip for now
				</Button>
				<Button
					colorScheme="brand"
					rounded={0}
					px={8}
					py={6}
					rightIcon={<FiArrowRight size="18px" />}
					onClick={handleDone}
					isLoading={isSavingAll}
					loadingText="Saving photos..."
					w={{ base: "full", lg: "auto" }}
				>
					{hasPendingImages ? "Save & continue" : "Done"}
				</Button>
			</Stack>
		</Stack>
	);
};

interface ItemImageCardProps {
	title: string;
	existingCount: number;
	pendingImages: PendingImage[];
	isSavingAll: boolean;
	canAddMore: boolean;
	onAddClick: () => void;
	onRemove: (index: number) => void;
	inputRef: (el: HTMLInputElement | null) => void;
	onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ItemImageCard: React.FC<ItemImageCardProps> = ({
	title,
	existingCount,
	pendingImages,
	isSavingAll,
	canAddMore,
	onAddClick,
	onRemove,
	inputRef,
	onFileSelect,
}) => (
	<Box border="1px solid #E7E7E7" p={4}>
		<input
			type="file"
			accept="image/jpeg,image/png"
			multiple
			style={{ display: "none" }}
			ref={inputRef}
			onChange={onFileSelect}
		/>
		<Stack
			direction={{ base: "column", md: "row" }}
			justify="space-between"
			align={{ base: "stretch", md: "center" }}
			mb={pendingImages.length > 0 ? 3 : 0}
		>
			<Box>
				<Text fontWeight="medium">{title}</Text>
				{existingCount > 0 && (
					<Text fontSize="xs" color="gray.500">
						{existingCount} existing photo
						{existingCount > 1 ? "s" : ""}
					</Text>
				)}
			</Box>
			{canAddMore && (
				<Button
					leftIcon={<FiPlus size="18px" />}
					colorScheme="earthRed"
					size="sm"
					variant="outline"
					rounded={0}
					onClick={onAddClick}
					isDisabled={isSavingAll}
					w={{ base: "full", md: "auto" }}
				>
					Add photo(s)
				</Button>
			)}
		</Stack>

		{pendingImages.length > 0 && (
			<Stack spacing={2}>
				{isSavingAll && <Progress size="xs" isIndeterminate />}
				<HStack flexWrap="wrap" gap={2}>
					{pendingImages.map((img, index) => (
						<Box key={index} position="relative">
							<Image
								src={img.preview}
								w="80px"
								h="80px"
								objectFit="cover"
								border="1px solid #E7E7E7"
							/>
							<IconButton
								aria-label="Remove photo"
								icon={<FiTrash2 size="12px" />}
								size="xs"
								colorScheme="red"
								variant="solid"
								position="absolute"
								top="2px"
								right="2px"
								onClick={() => onRemove(index)}
								isDisabled={isSavingAll}
							/>
						</Box>
					))}
				</HStack>
			</Stack>
		)}
	</Box>
);
